import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Copy,
  Check,
  Tag,
  Percent,
  DollarSign,
  Users,
  Calendar,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDiscountCodes({ user }) {
  const [codes, setCodes] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, code: null });
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    code: "",
    code_type: "custom",
    discount_type: "member_rate",
    discount_value: "",
    term_ids: [],
    expires_at: "",
    max_uses: "",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, termsRes] = await Promise.all([
        axios.get(`${API}/academy/admin/discount-codes`, { withCredentials: true }),
        axios.get(`${API}/academy/terms`, { withCredentials: true }),
      ]);
      setCodes(codesRes.data || []);
      setTerms(termsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load discount codes");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      code_type: "custom",
      discount_type: "member_rate",
      discount_value: "",
      term_ids: [],
      expires_at: "",
      max_uses: "",
      description: "",
    });
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
      };
      
      await axios.post(`${API}/academy/admin/discount-codes`, payload, { withCredentials: true });
      toast.success("Discount code created!");
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating code:", error);
      toast.error(error.response?.data?.detail || "Failed to create discount code");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.code) return;
    setSaving(true);
    try {
      const payload = {
        discount_type: formData.discount_type,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        term_ids: formData.term_ids,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
        description: formData.description,
        is_active: true,
      };
      
      await axios.put(`${API}/academy/admin/discount-codes/${editDialog.code.id}`, payload, { withCredentials: true });
      toast.success("Discount code updated!");
      setEditDialog({ open: false, code: null });
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error updating code:", error);
      toast.error(error.response?.data?.detail || "Failed to update discount code");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (codeId) => {
    if (!confirm("Are you sure you want to deactivate this discount code?")) return;
    try {
      await axios.delete(`${API}/academy/admin/discount-codes/${codeId}`, { withCredentials: true });
      toast.success("Discount code deactivated");
      fetchData();
    } catch (error) {
      console.error("Error deactivating code:", error);
      toast.error("Failed to deactivate discount code");
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success("Code copied to clipboard!");
  };

  const openEditDialog = (code) => {
    setFormData({
      code: code.code,
      code_type: code.code_type,
      discount_type: code.discount_type,
      discount_value: code.discount_value?.toString() || "",
      term_ids: code.term_ids || [],
      expires_at: code.expires_at ? code.expires_at.split("T")[0] : "",
      max_uses: code.max_uses?.toString() || "",
      description: code.description || "",
    });
    setEditDialog({ open: true, code });
  };

  const getDiscountDisplay = (code) => {
    switch (code.discount_type) {
      case "member_rate":
        return <Badge className="bg-emerald-500/20 text-emerald-400">Member Rates</Badge>;
      case "percentage":
        return <Badge className="bg-blue-500/20 text-blue-400">{code.discount_value}% Off</Badge>;
      case "fixed_amount":
        return <Badge className="bg-purple-500/20 text-purple-400">${code.discount_value} Off</Badge>;
      default:
        return <Badge className="bg-slate-500/20">{code.discount_type}</Badge>;
    }
  };

  const isExpired = (code) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiry";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Tag className="h-6 w-6 text-amber-400" />
                Discount Codes
              </h1>
              <p className="text-slate-400 text-sm">Create and manage promotional codes</p>
            </div>
          </div>
          <Button
            onClick={() => { resetForm(); setCreateDialog(true); }}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Code
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-white">{codes.length}</div>
            <div className="text-sm text-slate-400">Total Codes</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">
              {codes.filter(c => c.is_active && !isExpired(c)).length}
            </div>
            <div className="text-sm text-slate-400">Active</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-blue-400">
              {codes.reduce((acc, c) => acc + (c.used_count || 0), 0)}
            </div>
            <div className="text-sm text-slate-400">Total Uses</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-amber-400">
              {codes.filter(c => c.discount_type === "member_rate").length}
            </div>
            <div className="text-sm text-slate-400">Member Rate Codes</div>
          </Card>
        </div>

        {/* Codes Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No discount codes yet</p>
              <p className="text-sm">Create your first code to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Code</TableHead>
                  <TableHead className="text-slate-400">Discount</TableHead>
                  <TableHead className="text-slate-400">Terms</TableHead>
                  <TableHead className="text-slate-400">Usage</TableHead>
                  <TableHead className="text-slate-400">Expires</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((code) => (
                  <TableRow key={code.id} className="border-slate-700">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-900 px-2 py-1 rounded font-mono text-amber-400">
                          {code.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(code.code)}
                          className="h-6 w-6 p-0"
                        >
                          {copiedCode === code.code ? (
                            <Check className="h-3 w-3 text-emerald-400" />
                          ) : (
                            <Copy className="h-3 w-3 text-slate-400" />
                          )}
                        </Button>
                      </div>
                      {code.description && (
                        <p className="text-xs text-slate-400 mt-1">{code.description}</p>
                      )}
                    </TableCell>
                    <TableCell>{getDiscountDisplay(code)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {code.term_names?.join(", ") || "All Terms"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span>{code.used_count || 0}</span>
                        {code.max_uses && (
                          <span className="text-slate-400">/ {code.max_uses}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className={isExpired(code) ? "text-red-400" : ""}>
                          {formatDate(code.expires_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {!code.is_active ? (
                        <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                      ) : isExpired(code) ? (
                        <Badge className="bg-orange-500/20 text-orange-400">Expired</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(code)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {code.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(code.id)}
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Create Discount Code</DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a new promotional code for parents
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Code Type */}
              <div className="space-y-2">
                <Label>Code Type</Label>
                <Select
                  value={formData.code_type}
                  onValueChange={(v) => setFormData({ ...formData, code_type: v, code: v === "random" ? "" : formData.code })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="custom">Custom Code</SelectItem>
                    <SelectItem value="random">Random Code</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Code Input */}
              {formData.code_type === "custom" && (
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    placeholder="e.g., SUMMER2025"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="bg-slate-800 border-slate-700 font-mono"
                  />
                </div>
              )}

              {/* Discount Type */}
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="member_rate">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-emerald-400" />
                        Member Rates (for non-members)
                      </div>
                    </SelectItem>
                    <SelectItem value="percentage">
                      <div className="flex items-center gap-2">
                        <Percent className="h-4 w-4 text-blue-400" />
                        Percentage Off
                      </div>
                    </SelectItem>
                    <SelectItem value="fixed_amount">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-purple-400" />
                        Fixed Amount Off
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Discount Value */}
              {formData.discount_type !== "member_rate" && (
                <div className="space-y-2">
                  <Label>
                    {formData.discount_type === "percentage" ? "Percentage" : "Amount ($)"}
                  </Label>
                  <Input
                    type="number"
                    placeholder={formData.discount_type === "percentage" ? "e.g., 15" : "e.g., 50"}
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              )}

              {/* Term Restriction */}
              <div className="space-y-2">
                <Label>Limit to Terms (optional)</Label>
                <Select
                  value={formData.term_ids.length > 0 ? formData.term_ids[0] : "all"}
                  onValueChange={(v) => setFormData({ ...formData, term_ids: v === "all" ? [] : [v] })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="All Terms" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all">All Terms</SelectItem>
                    {terms.map((term) => (
                      <SelectItem key={term.id} value={term.id}>
                        {term.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Expiry Date */}
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              {/* Max Uses */}
              <div className="space-y-2">
                <Label>Max Uses (leave empty for unlimited)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description (internal note)</Label>
                <Textarea
                  placeholder="e.g., Summer promotion for new parents"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={saving || (formData.code_type === "custom" && !formData.code)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, code: open ? editDialog.code : null })}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Discount Code</DialogTitle>
              <DialogDescription className="text-slate-400">
                Update code: <code className="text-amber-400">{editDialog.code?.code}</code>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Discount Type */}
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="member_rate">Member Rates</SelectItem>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.discount_type !== "member_rate" && (
                <div className="space-y-2">
                  <Label>{formData.discount_type === "percentage" ? "Percentage" : "Amount ($)"}</Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <div className="space-y-2">
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, code: null })}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
